import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Table, Stack } from 'react-bootstrap';
import Papa from 'papaparse';
import apiClient from '../api'; // Import apiClient

const BulkUploadModal = ({ show, handleClose, onUploadSuccess }) => { // Changed prop
  const [csvText, setCsvText] = useState('');
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false); // New state for loading indicator
  const [uploadError, setUploadError] = useState(null); // New state for upload errors

  const SUB_CATEGORIES_EXPENSE = [
    "Arriendo", "Cafetería y Snacks", "Combustible", "Comidas de Negocios", 
    "Comisiones Bancarias", "Equipos Tecnológicos", "Estacionamientos", 
    "Eventos Corporativos", "Insumos de Aseo", "Insumos de Oficina", "Internet", 
    "Lavado de Vehículos", "Legales y Notaría", "Mantenimiento Vehículos", 
    "Otros", "Patentes y Permisos", "Peajes y TAG", "Previred", 
    "Redes Sociales", "Servicios Básicos", "Suscripciones de Software", 
    "Telefonía Móvil/Fija", "Viajes de Negocios"
  ];

  const SUB_CATEGORIES_FINANCIAL = [
    "Compra Divisas (USD)", "Devolución de Gasto", "Inversión DAP", "Préstamo / Socios", "Remesa Internacional", "Transferencia Interna"
  ];

  const csvTemplate = "tipo,monto,descripcion,fecha,fecha_imputacion,categoria,subcategoria,boleta_factura,solicitud_liquidacion,impuesto,origen_impuesto,observaciones,empresa_conductor,comision_tc,intereses_dap,penalizaciones_booking\ningreso,100000,Venta de servicios,2025-09-30,2025-09-30,Conductores,,F-001,SOL-123,19000,IVA,Pago de cliente,Taxi Express S.A.,1500,0,500\negreso,50000,Compra de insumos,2025-09-25,2025-09-01,Gasto,Insumos de Oficina,REQ-456,9500,IVA,Material de oficina,Juan Pérez,0,0,0";

  const handleDownloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "plantilla_transacciones.csv");
      document.body.appendChild(link);
      link.click();
      // A small delay to ensure the download is initiated before removing the link
      setTimeout(() => {
        document.body.removeChild(link);
      }, 0); // 0ms delay, but allows browser to process click event first
    }
  };

  const validateTransaction = (row, index) => {
    const newErrors = [];
    const transaction = {};

    // Type validation
    if (!row.tipo || !['ingreso', 'egreso'].includes(row.tipo.toLowerCase())) {
      newErrors.push(`Línea ${index + 1}: El tipo debe ser 'ingreso' o 'egreso'.`);
    }
    transaction.type = row.tipo ? row.tipo.toLowerCase() : '';

    // Amount validation
    const amount = parseFloat(row.monto);
    if (isNaN(amount) || amount <= 0) {
      newErrors.push(`Línea ${index + 1}: El monto debe ser un número positivo válido.`);
    }
    transaction.amount = amount;

    // Description validation
    if (!row.descripcion || row.descripcion.trim() === '') {
      newErrors.push(`Línea ${index + 1}: La descripción no puede estar vacía.`);
    }
    transaction.description = row.descripcion || '';

    // Date validation (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!row.fecha || !dateRegex.test(row.fecha)) {
      newErrors.push(`Línea ${index + 1}: La fecha debe tener el formato YYYY-MM-DD.`);
    }
    transaction.date = row.fecha || '';

    // Accrual Date validation (optional, but if present must be YYYY-MM-DD)
    if (row.fecha_imputacion && !dateRegex.test(row.fecha_imputacion)) {
        newErrors.push(`Línea ${index + 1}: La fecha de imputación debe tener el formato YYYY-MM-DD.`);
    }
    transaction.accrual_date = row.fecha_imputacion || null;

    // Category (optional, default to 'Otros')
    transaction.category = row.categoria || 'Otros';
    
    // Subcategory validation
    const subcatRaw = (row.subcategoria || '').trim();
    transaction.subcategory = subcatRaw;

    if (transaction.category === 'Gasto') {
        if (!subcatRaw) {
             newErrors.push(`Línea ${index + 1}: La categoría 'Gasto' requiere una subcategoría.`);
        } else if (!SUB_CATEGORIES_EXPENSE.includes(subcatRaw)) {
             newErrors.push(`Línea ${index + 1}: Subcategoría '${subcatRaw}' no válida para 'Gasto'. Opciones: ${SUB_CATEGORIES_EXPENSE.join(', ')}`);
        }
    } else if (transaction.category === 'Movimiento Financiero') {
        if (!subcatRaw) {
             newErrors.push(`Línea ${index + 1}: La categoría 'Movimiento Financiero' requiere una subcategoría.`);
        } else if (!SUB_CATEGORIES_FINANCIAL.includes(subcatRaw)) {
             newErrors.push(`Línea ${index + 1}: Subcategoría '${subcatRaw}' no válida. Opciones: ${SUB_CATEGORIES_FINANCIAL.join(', ')}`);
        }
    }

    // Optional fields - Mapped from CSV headers to backend schema names
    transaction.invoice_number = row.boleta_factura || '';
    transaction.request_id = row.solicitud_liquidacion || '';
    transaction.tax = parseFloat(row.impuesto) || 0;
    transaction.tax_origin = row.origen_impuesto || '';
    transaction.notes = row.observaciones || '';
    transaction.entity_name = row.empresa_conductor || '';
    transaction.cc_commission = parseFloat(row.comision_tc) || 0;
    transaction.dap_interest = parseFloat(row.intereses_dap) || 0;
    transaction.penalizaciones_booking = parseFloat(row.penalizaciones_booking) || 0;
    transaction.completed = false; // New transactions are not completed by default

    return { transaction, newErrors };
  };

  const parseCsv = (csvString) => {
    Papa.parse(csvString, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const newParsedData = [];
        let allErrors = [];

        results.data.forEach((row, index) => {
          const { transaction, newErrors } = validateTransaction(row, index);
          if (newErrors.length > 0) {
            allErrors = allErrors.concat(newErrors);
          } else {
            newParsedData.push(transaction);
          }
        });

        setParsedData(newParsedData);
        setErrors(allErrors);
      },
      error: (err) => {
        setErrors([`Error al parsear CSV: ${err.message}`]);
      }
    });
  };

  const handleProcessText = () => {
    parseCsv(csvText);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setCsvText(''); // Clear text area if file is selected
    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = (event) => {
        parseCsv(event.target.result);
      };
      reader.readAsText(selectedFile);
    }
  };

  const handleAddParsedTransactions = async () => {
    if (errors.length === 0 && parsedData.length > 0) {
      setUploading(true);
      setUploadError(null);
      try {
        await apiClient.post('/transactions/bulk', { transactions: parsedData });
        alert("Transacciones cargadas masivamente con éxito.");
        handleModalClose(); // Close and reset state
        if (onUploadSuccess) {
          onUploadSuccess(); // Notify parent to refresh transactions
        }
      } catch (error) {
        console.error("Error al cargar transacciones masivamente:", error);
        setUploadError("Error al cargar transacciones. Por favor, inténtalo de nuevo.");
      } finally {
        setUploading(false);
      }
    } else if (errors.length > 0) {
      alert("Corrige los errores antes de procesar las transacciones.");
    } else {
      alert("No hay transacciones válidas para agregar.");
    }
  };

  const handleModalClose = () => {
    setCsvText('');
    setParsedData([]);
    setErrors([]);
    setUploading(false); // Reset uploading state
    setUploadError(null); // Reset upload error
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Carga Masiva de Transacciones</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Stack gap={3}>
          <Alert variant="info">
            Puedes cargar transacciones pegando texto CSV directamente o subiendo un archivo.
            <Button variant="link" onClick={handleDownloadTemplate} className="p-0 ms-2">Descargar Plantilla CSV</Button>
          </Alert>

          <Form.Group controlId="csvTextarea">
            <Form.Label>Pegar texto CSV aquí:</Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder={csvTemplate}
            />
            <Button variant="primary" className="mt-2" onClick={handleProcessText} disabled={uploading}>
              Procesar Texto
            </Button>
          </Form.Group>

          <hr />

          <Form.Group controlId="csvFile">
            <Form.Label>O subir archivo CSV:</Form.Label>
            <Form.Control type="file" accept=".csv" onChange={handleFileChange} disabled={uploading} />
          </Form.Group>

          {uploadError && <Alert variant="danger">{uploadError}</Alert>}

          {errors.length > 0 && (
            <Alert variant="danger">
              <h5>Errores encontrados:</h5>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {parsedData.length > 0 && errors.length === 0 && (
            <Alert variant="success">
              <h5>Vista Previa de Transacciones Válidas ({parsedData.length} encontradas):</h5>
              <Table striped bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Monto</th>
                    <th>Descripción</th>
                    <th>Fecha</th>
                    <th>F. Imputación</th>
                    <th>Categoría</th>
                    <th>Subcategoría</th>
                    <th>Boleta/Factura</th>
                    <th>Solicitud/Liquidación</th>
                    <th>Impuesto</th>
                    <th>Origen Imp.</th>
                    <th>Observaciones</th>
                    <th>Empresa/Conductor</th>
                    <th>Comisión T/C</th>
                    <th>Intereses DAP</th>
                    <th>Penalización Booking</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.map((t, index) => (
                    <tr key={index}>
                      <td>{t.type}</td>
                      <td>{t.amount}</td>
                      <td>{t.description}</td>
                      <td>{t.date}</td>
                      <td>{t.accrual_date}</td>
                      <td>{t.category}</td>
                      <td>{t.subcategory}</td>
                      <td>{t.invoice_number}</td>
                      <td>{t.request_id}</td>
                      <td>{t.tax}</td>
                      <td>{t.tax_origin}</td>
                      <td>{t.notes}</td>
                      <td>{t.entity_name}</td>
                      <td>{t.cc_commission}</td>
                      <td>{t.dap_interest}</td>
                      <td>{t.penalizaciones_booking}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Alert>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose} disabled={uploading}>Cerrar</Button>
        <Button variant="success" onClick={handleAddParsedTransactions} disabled={errors.length > 0 || parsedData.length === 0 || uploading}>
          {uploading ? 'Cargando...' : 'Agregar Transacciones'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkUploadModal;
